import React,{useState,useEffect} from "react";
import {
  Box,
  Drawer,
  IconButton,
  Divider,
  Chip,
  Typography
} from "@mui/material";
import CustomText, { textStyles } from "../../../../components/CustomText";
import profilecross from "../../../../assets/profilecross.svg";
import down from "../../../../assets/down.svg";
import checkin from "../../../../assets/Attendance/checkin.svg";
import checkout from "../../../../assets/Attendance/checkout.svg";
import map from "../../../../assets/Attendance/map.svg";    
import CustomButton from "../../../../components/CustomButton";
import maparrow from "../../../../assets/maparrow.svg";
import { useTranslation } from "react-i18next";
import { showUnderDevelopment } from "../../../../components/Alert.js";
import valid from "../../../../assets/Attendance/valid.svg";
import StatusDropdown from "../../../../components/StatusDropdown.js";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
 import CloseIcon from "@mui/icons-material/Close";
import dummy from "../../../../assets/dummy.png"
import { convertUTCToLocalDateTime } from "../../../../components/dateTimeUtils";
import {
  getGoogleMapsPinIconForStatus,
  getStatusBadgeBackgroundColor,
  getStatusBadgeForegroundColor,
  resolvePunchPinStatusKey,
} from "../../../../constants/statusPalette";
import { getAttendanceHistoryColorKey } from "./attendanceHistoryStatusOptions";

const REVIEW_STATUS_LABEL_MAP = {
  Validated: "unvalidatedPunches.statusLabels.validatedPunch",
  Invalid: "unvalidatedPunches.statusLabels.unvalidatedPunch",
  Pending: "unvalidatedPunches.statusLabels.pendingPunch",
};

const mapReviewStatusToUI = (reviewStatus) => {
  if (!reviewStatus) return null;
  switch (String(reviewStatus).trim().toUpperCase()) {
    case "APPROVED":
      return "Validated";
    case "REJECTED":
      return "Invalid";
    case "PENDING":
      return "Pending";
    default:
      return null;
  }
};

const mapContainerStyle = { width: "100%", height: "100%" };

/** Same local time display as Today's Logs drawer (12h, en-GB) via dateTimeUtils. */
const LOCAL_TIME_DISPLAY = {
  locale: "en-GB",
  formatOptions: {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  },
};

// Section header — status dot uses same fill as StatusDropdown badge
const SectionHeader = ({ label, icon, sectionicon, statusKey }) => (
  <Box mt={2.5} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    {statusKey ? (
      <Box
        aria-hidden
        sx={{
          width: 20,
          height: 20,
          borderRadius: "4px",
          flexShrink: 0,
          backgroundColor: getStatusBadgeBackgroundColor(statusKey),
        }}
      />
    ) : (
      sectionicon && (
        <img src={sectionicon} width={20} height={20} alt="section icon" />
      )
    )}
    <CustomText
      sx={{
        ...textStyles.h1,
        fontSize: 18,
        fontWeight: 600,
        color: "#000000",
      }}
    >
      {label}
    </CustomText>
    {/* <img src={icon} width={20} height={20} alt="dropdown icon" /> */}
  </Box>
);

// Styled box wrapper
const InfoBox = ({ children }) => (
  <Box
    mt={2}
    sx={{
      border: "1px solid #091E4224",
      borderRadius: "10px",
      px: 2,
      py: 2,
    }}
  >
    {children}
  </Box>
);

// Row for key-value info
const InfoRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mb: 2,
    }}
  >
    <CustomText
      sx={{
        ...textStyles.h1,
        fontSize: 16,
        fontWeight: 400,
        color: "#5E5C5C",
        textAlign: "left",
      }}
    >
      {label}
    </CustomText>
    <Box sx={{ maxWidth: "60%", textAlign: "right" }}>
      {typeof value === "string" || typeof value === "number" ? (
        <CustomText
          sx={{
            ...textStyles.h1,
            fontSize: 15,
            fontWeight: 400,
            color: "#172B4D",
            textAlign: "right",
            wordBreak: "break-word",
          }}
        >
          {value || "--"}
        </CustomText>
      ) : (
        value || "--"
      )}
    </Box>
  </Box>
);


export default function AttendanceDetailsDrawer({ open, onClose, selectedRow }) {
  const { t } = useTranslation();
const formatTimeTo12Hour = (timeString) => {
  const localValue = convertUTCToLocalDateTime(timeString, LOCAL_TIME_DISPLAY);
  return localValue === "—" ? "--" : localValue;
};
const normalizeStatusKey = (status) =>
  status
    ?.toLowerCase()
    .replace(/\s+/g, "")       // "Early Out" → "earlyout"
    .replace(/-/g, "");   
    
const formatStatus = (status) => {
  const keyMap = {
    present: "attendanceStatus.present",
    earlyout: "attendanceStatus.early_clock_out",
    latearrival: "attendanceStatus.late_clock_in",
    lateout: "attendanceStatus.late_clock_out",
    lateandearlyout: "attendanceStatus.lateAndEarlyOut",
    leave: "attendanceStatus.leave",
    absent: "attendanceStatus.absent",
    halfleave: "attendanceStatus.halfLeave",
    hasissues: "attendanceStatus.unvalidatedPunches",
    halfday: "attendanceStatus.halfDay",
    overtime: "attendanceStatus.overtime"
  };

  const normalized = normalizeStatusKey(status);
  return keyMap[normalized] ? t(keyMap[normalized]) : status || "--";
}; 
 const [isFullMapOpen, setIsFullMapOpen] = useState(false);
 const [activePunchIndex, setActivePunchIndex] = useState(null);
 const fullMapRef = React.useRef(null);
const punches = selectedRow?.raw?.punches || [];
const orderedPunches = punches.filter((p) =>
  ["CLOCK_IN", "BREAK_START", "BREAK_END", "CLOCK_OUT"].includes(p?.actionType)
);
const sortedOrderedPunches = [...orderedPunches].sort((a, b) => {
  const aTime = new Date(a?.occurredAt || 0).getTime();
  const bTime = new Date(b?.occurredAt || 0).getTime();
  return bTime - aTime;
});

const getLatestPunchOccurredAt = (actionType) => {
  const matching = punches.filter((p) => p?.actionType === actionType);
  if (!matching.length) return null;
  return matching.reduce((latest, punch) => {
    const latestMs = new Date(latest?.occurredAt || 0).getTime();
    const punchMs = new Date(punch?.occurredAt || 0).getTime();
    return punchMs >= latestMs ? punch : latest;
  }).occurredAt ?? null;
};

const getLatestTimestamp = (timestamps = []) => {
  const valid = timestamps.filter(Boolean);
  if (!valid.length) return null;
  return valid.reduce((latest, ts) => {
    return new Date(ts).getTime() >= new Date(latest).getTime() ? ts : latest;
  }, valid[0]);
};

const latestClockInAt =
  getLatestPunchOccurredAt("CLOCK_IN") ??
  getLatestTimestamp(selectedRow?.checkIns) ??
  (selectedRow?.checkIn && selectedRow.checkIn !== "—" ? selectedRow.checkIn : null);

const latestClockOutAt =
  getLatestPunchOccurredAt("CLOCK_OUT") ??
  getLatestTimestamp(selectedRow?.checkOuts) ??
  (selectedRow?.checkOut && selectedRow.checkOut !== "—" ? selectedRow.checkOut : null);

const rowIssuesBlock = selectedRow?.raw?.issues ?? null;

const validPunches = punches.filter(
  (p) => p?.location?.gpsLatitude && p?.location?.gpsLongitude
);

const normalizeAttendanceStatus = (text = "") =>
  String(text).toUpperCase().replace(/\s+/g, "_").replace(/-/g, "_");

const renderValidationBadge = (isValid) => {
  if (typeof isValid !== "boolean") return "--";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 78,
        px: 1.25,
        py: 0.35,
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: 600,
        fontFamily: "Poppins, sans-serif",
        color: isValid ? "#216E4E" : "#A12622",
        backgroundColor: isValid ? "#DCF3E8" : "#FDE8E8",
        border: isValid ? "1px solid #9BD3BA" : "1px solid #F4B6B6",
      }}
    >
      {isValid ? t("attendanceDrawer.valid") : t("attendanceDrawer.invalid")}
    </Box>
  );
};

/** API schedule strings (e.g. "09:00:00") — same handling as Today's Logs drawer. */
const formatScheduleWallTime = (timeValue) => {
  if (timeValue === null || timeValue === undefined || timeValue === "") return "--";
  const localValue = convertUTCToLocalDateTime(timeValue, LOCAL_TIME_DISPLAY);
  return localValue === "—" ? "--" : localValue;
};

const formatScheduleClassification = (classification) => {
  const normalized = String(classification || "").trim().toUpperCase();

  if (!normalized || normalized === "NOT_APPLICABLE") {
    return "--";
  }

  const supportedClassifications = new Set([
    "EARLY_CLOCK_IN",
    "ON_TIME_CLOCK_IN",
    "LATE_CLOCK_IN",
    "EARLY_CLOCK_OUT",
    "ON_TIME_CLOCK_OUT",
    "LATE_CLOCK_OUT",
  ]);

  if (!supportedClassifications.has(normalized)) {
    return "--";
  }

  return t(`attendanceDrawer.attendanceIssueValues.${normalized}`, {
    defaultValue: normalized
      .split("_")
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(" "),
  });
};

const getScheduleMatchKind = (classification) => {
  const normalized = String(classification || "").trim().toUpperCase();
  if (!normalized || normalized === "NOT_APPLICABLE") return "na";
  if (normalized === "ON_TIME_CLOCK_IN" || normalized === "ON_TIME_CLOCK_OUT") return "valid";
  if (
    normalized === "LATE_CLOCK_IN" ||
    normalized === "EARLY_CLOCK_IN" ||
    normalized === "EARLY_CLOCK_OUT" ||
    normalized === "LATE_CLOCK_OUT"
  ) {
    return "invalid";
  }
  return "na";
};

const renderScheduleMatchBadge = (kind) => {
  if (kind === "valid") return renderValidationBadge(true);
  if (kind === "invalid") return renderValidationBadge(false);
  return "--";
};

const formatMinutesDurationLabel = (minutes) => {
  if (minutes === null || minutes === undefined || Number.isNaN(Number(minutes))) return "--";
  const total = Math.max(0, Math.round(Number(minutes)));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h > 0 && m > 0) {
    return h === 1
      ? t("attendanceDrawer.durationOneHourAndMinutes", { minutes: m })
      : t("attendanceDrawer.durationHoursMinutes", { hours: h, minutes: m });
  }
  if (h > 0) {
    return h === 1
      ? t("attendanceDrawer.durationHourOnly")
      : t("attendanceDrawer.durationHoursOnly", { hours: h });
  }
  return t("attendanceDrawer.durationMinutesOnly", { count: m });
};

const formatIssueTimeDifference = (issue) => {
  const mins = issue?.minutesDifference ?? issue?.minutes_difference;
  if (mins != null && !Number.isNaN(Number(mins))) {
    const duration = formatMinutesDurationLabel(Math.abs(Number(mins)));
    const direction = String(issue?.direction || "").toLowerCase();
    if (direction === "early") return `${duration} early`;
    if (direction === "late") return `${duration} late`;
    return duration;
  }
  return issue?.timeDifference || issue?.time_difference || null;
};

const isClockOutScheduleContext = (classificationOrActionType) => {
  const token = String(classificationOrActionType || "").toUpperCase();
  return (
    token.includes("CLOCK_OUT") ||
    token === "EARLY_CLOCK_OUT" ||
    token === "LATE_CLOCK_OUT" ||
    token === "ON_TIME_CLOCK_OUT"
  );
};

const formatMinutesFromSchedule = (minutes, classificationOrActionType) => {
  if (minutes === null || minutes === undefined || Number.isNaN(Number(minutes))) {
    return "--";
  }
  const n = Number(minutes);
  if (n === 0) return t("attendanceDrawer.minutesOnSchedule");
  const durationLabel = formatMinutesDurationLabel(Math.abs(n));
  const isClockOut = isClockOutScheduleContext(classificationOrActionType);
  if (n > 0) {
    return isClockOut
      ? t("attendanceDrawer.minutesLateVsScheduleEnd", { duration: durationLabel })
      : t("attendanceDrawer.minutesLateVsSchedule", { duration: durationLabel });
  }
  return isClockOut
    ? t("attendanceDrawer.minutesEarlyVsScheduleEnd", { duration: durationLabel })
    : t("attendanceDrawer.minutesEarlyVsSchedule", { duration: durationLabel });
};

const formatScheduleMinutesForPunch = (punch) => {
  const scheduleContext =
    punch?.schedule_timing?.classification || punch?.actionType;
  return formatMinutesFromSchedule(
    punch?.schedule_timing?.minutesFromSchedule,
    scheduleContext
  );
};

const formatScheduledWindow = (st) => {
  const start = st?.scheduleStart;
  const end = st?.scheduleEnd;
  const grace = st?.graceMinutes;
  if (!start || !end) return "--";
  const startDisp = formatScheduleWallTime(start);
  const endDisp = formatScheduleWallTime(end);
  if (startDisp === "--" || endDisp === "--") {
    return grace !== null && grace !== undefined
      ? t("attendanceDrawer.scheduledWindowValue", { start, end, grace })
      : `${start} – ${end}`;
  }
  return grace !== null && grace !== undefined
    ? t("attendanceDrawer.scheduledWindowValue", {
        start: startDisp,
        end: endDisp,
        grace,
      })
    : `${startDisp} – ${endDisp}`;
};

const formatIssueListText = (issues) => {
  if (!issues?.issue_list?.length) return "--";
  return issues.issue_list
    .map((key) =>
      t(`attendanceDrawer.issueKeys.${key}`, {
        defaultValue: String(key).replace(/_/g, " "),
      })
    )
    .join(", ");
};

const formatIssueBreakdownText = (breakdown) => {
  if (!breakdown || typeof breakdown !== "object") return "--";
  const keys = Object.entries(breakdown)
    .filter(([, count]) => count != null && Number(count) > 0)
    .map(([key]) => key);
  if (!keys.length) return "--";
  return keys
    .map((key) =>
      t(`attendanceDrawer.issueKeys.${key}`, {
        defaultValue: String(key).replace(/_/g, " "),
      })
    )
    .join(", ");
};

const formatAttendanceIssuesDetail = (attendanceIssues) => {
  if (!Array.isArray(attendanceIssues) || !attendanceIssues.length) return "--";
  return attendanceIssues
    .map((issue) => {
      const label =
        issue?.label ||
        t(`attendanceDrawer.issueKeys.${issue?.type}`, {
          defaultValue: String(issue?.type || "").replace(/_/g, " "),
        });
      const time = formatIssueTimeDifference(issue);
      return time ? `${label} (${time})` : label;
    })
    .join(", ");
};

const formatPunchIssuesDetail = (punch) => {
  const issues = punch?.issues;
  if (!issues) return "--";
  const fromAttendance = formatAttendanceIssuesDetail(issues.attendance_issues);
  if (fromAttendance !== "--") return fromAttendance;
  return formatIssueListText(issues);
};

const formatDayIssueBreakdown = () => {
  const breakdown = rowIssuesBlock?.punch_issue_breakdown;
  if (breakdown && Object.keys(breakdown).length) {
    return formatIssueBreakdownText(breakdown);
  }
  const summary = selectedRow?.raw?.details?.issueSummary;
  if (!summary) return "--";
  return formatIssueBreakdownText({
    face_not_matched: summary.face_not_matched_count,
    location_not_matched: summary.location_not_matched_count,
    requires_review: summary.requires_review_count,
    late_clock_in: summary.late_clock_in_count,
    early_clock_in: summary.early_clock_in_count,
    early_clock_out: summary.early_clock_out_count,
    late_clock_out: summary.late_clock_out_count,
  });
};

const resolveTotalIssueCount = () => {
  if (rowIssuesBlock?.total_issue_count != null) {
    return rowIssuesBlock.total_issue_count;
  }
  if (rowIssuesBlock?.total_punch_issue_count != null) {
    return rowIssuesBlock.total_punch_issue_count;
  }
  if (selectedRow?.raw?.attendance?.issuesCount != null) {
    return selectedRow.raw.attendance.issuesCount;
  }
  return null;
};

const punchHasIssues = (punch) => {
  const issues = punch?.issues;
  if (!issues) return false;
  return (
    issues.has_any_issue === true ||
    (typeof issues.issue_count === "number" && issues.issue_count > 0) ||
    (issues.issue_list?.length > 0) ||
    (issues.attendance_issues?.length > 0)
  );
};

const resolveWorkingMinutesDisplay = (minutes, hoursDecimal) => {
  if (minutes !== null && minutes !== undefined && !Number.isNaN(Number(minutes))) {
    return formatMinutesDurationLabel(Number(minutes));
  }
  if (hoursDecimal !== null && hoursDecimal !== undefined && !Number.isNaN(Number(hoursDecimal))) {
    return formatMinutesDurationLabel(Math.round(Number(hoursDecimal) * 60));
  }
  return "--";
};

const formatScheduledWorkingFromSchedule = (schedule) => {
  const fromScheduled = resolveWorkingMinutesDisplay(
    schedule?.scheduledWorkingMinutes,
    schedule?.scheduledWorkingHours
  );
  if (fromScheduled !== "--") return fromScheduled;
  return formatMinutesDurationLabel(schedule?.workingHoursMinutes);
};

const formatCompletedWorkingFromSchedule = (schedule) => {
  const mins = schedule?.completedWorkingMinutes;
  const hrs = schedule?.completedWorkingHours;
  const hasMins = mins !== null && mins !== undefined && !Number.isNaN(Number(mins));
  const hasHrs = hrs !== null && hrs !== undefined && !Number.isNaN(Number(hrs));
  if (!hasMins && !hasHrs) return "--";
  return resolveWorkingMinutesDisplay(hasMins ? mins : null, hasHrs ? hrs : null);
};

const formatScheduledBreakFromSchedule = (schedule) => {
  const raw = schedule?.breakDurationMinutes ?? schedule?.breakPolicy?.unpaid ?? null;
  if (raw === null || raw === undefined || Number.isNaN(Number(raw))) return "--";
  return formatMinutesDurationLabel(Number(raw));
};

const formatTakenBreakFromSchedule = (schedule) => {
  const raw = schedule?.takenBreakMinutes;
  if (raw === null || raw === undefined || Number.isNaN(Number(raw))) return "--";
  return formatMinutesDurationLabel(Number(raw));
};

const isAbsentStatusRow =
  normalizeAttendanceStatus(selectedRow?.status || "") === "ABSENT";

const buildRowScheduleSummary = () => {
  if (isAbsentStatusRow) return t("attendanceDrawer.rowScheduleSummaryAbsent");
  const latest = sortedOrderedPunches[0];
  const raw = selectedRow?.raw;
  const schedule = raw?.details?.schedule;
  const start = raw?.details?.scheduledStart ?? schedule?.startTime;
  const end = raw?.details?.scheduledEnd ?? schedule?.endTime;

  if (!latest) {
    if (start && end) {
      return t("attendanceDrawer.rowScheduleSummaryNoPunch", {
        start: formatScheduleWallTime(start),
        end: formatScheduleWallTime(end),
      });
    }
    return "--";
  }

  const outcome = formatScheduleClassification(latest.schedule_timing?.classification);
  const mins = formatScheduleMinutesForPunch(latest);

  const segments = [];
  if (outcome !== "--") segments.push(outcome);
  if (mins !== "--") segments.push(mins);
  return segments.length ? segments.join(" · ") : "--";
};

const InfoRowValue = ({ value }) => {
  if (typeof value === "string" || typeof value === "number") {
    return (
      <CustomText
        sx={{
          ...textStyles.h1,
          fontSize: 15,
          fontWeight: 400,
          color: "#172B4D",
          textAlign: "right",
          marginLeft: "auto",
          maxWidth: "65%",
          wordBreak: "break-word",
        }}
      >
        {value || "--"}
      </CustomText>
    );
  }
  return value || "--";
};

const DrawerInfoRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 2,
      mb: 2,
    }}
  >
    <CustomText
      sx={{
        ...textStyles.h1,
        fontSize: 16,
        fontWeight: 400,
        color: "#5E5C5C",
        textAlign: "left",
      }}
    >
      {label}
    </CustomText>
    <InfoRowValue value={value} />
  </Box>
);

const getRowStatusColorKey = () =>
  getAttendanceHistoryColorKey(selectedRow?.status);

const getPunchStatusForPin = (punch) =>
  resolvePunchPinStatusKey(punch, getRowStatusColorKey());

const getMarkerIcon = (status) => getGoogleMapsPinIconForStatus(status);

const fitMapToPunches = (map) => {
  if (!map || !window.google?.maps || !validPunches.length) return;

  if (validPunches.length === 1) {
    map.setCenter({
      lat: validPunches[0].location.gpsLatitude,
      lng: validPunches[0].location.gpsLongitude,
    });
    map.setZoom(15);
    return;
  }

  const bounds = new window.google.maps.LatLngBounds();
  validPunches.forEach((punch) => {
    bounds.extend({
      lat: punch.location.gpsLatitude,
      lng: punch.location.gpsLongitude,
    });
  });
  map.fitBounds(bounds, 40);
};

useEffect(() => {
  if (
    activePunchIndex !== null &&
    !validPunches[activePunchIndex]
  ) {
    setActivePunchIndex(null);
  }
}, [validPunches, activePunchIndex]);
const EvidenceBlock = ({ punch }) => {
  if (!punch) return null;

  return (
    <>
      <DrawerInfoRow
        label={t("attendanceDrawer.evidence")}
        value={
          punch?.evidence?.selfieUrl ? (
            <img
              crossOrigin="anonymous"
              src={punch.evidence.selfieUrl}
              alt="Evidence"
              style={{
                width: 60,
                height: 60,
                borderRadius: 8,
                objectFit: "cover",
                border: "1px solid #ddd",
              }}
              onError={(e) => {
                e.currentTarget.src = dummy;
              }}
            />
          ) : (
            "--"
          )
        }
      />
      <DrawerInfoRow
        label={t("attendanceDrawer.faceMatched")}
        value={renderValidationBadge(punch?.validation?.faceMatched)}
      />
      <DrawerInfoRow
        label={t("attendanceDrawer.locationValidated")}
        value={renderValidationBadge(
          punch?.validation?.locationMatched ?? punch?.validation?.locationValidated
        )}
      />
      <DrawerInfoRow
        label={t("attendanceDrawer.reviewStatus")}
        value={
          mapReviewStatusToUI(punch?.validation?.reviewStatus) ? (
            <Box sx={{ pointerEvents: "none" }}>
              <StatusDropdown
                currentStatus={mapReviewStatusToUI(punch?.validation?.reviewStatus)}
                labelMap={REVIEW_STATUS_LABEL_MAP}
              />
            </Box>
          ) : (
            "--"
          )
        }
      />
    </>
  );
};

const getPunchMeta = (actionType) => {
  switch (actionType) {
    case "CLOCK_IN":
      return {
        title: t("clockIn"),
        timeLabel: t("clockIn"),
        locationLabel: t("attendanceDrawer.checkInLocation"),
        icon: checkin,
      };
    case "BREAK_START":
      return {
        title: t("breakStart"),
        timeLabel: t("breakStart"),
        locationLabel: t("attendanceDrawer.breakStartLocation"),
        icon: checkin,
      };
    case "BREAK_END":
      return {
        title: t("breakEnd"),
        timeLabel: t("breakEnd"),
        locationLabel: t("attendanceDrawer.breakEndLocation"),
        icon: checkout,
      };
    case "CLOCK_OUT":
      return {
        title: t("clockOut"),
        timeLabel: t("clockOut"),
        locationLabel: t("attendanceDrawer.checkOutLocation"),
        icon: checkout,
      };
    default:
      return {
        title: actionType || "--",
        timeLabel: actionType || "--",
        locationLabel: t("attendanceDrawer.location"),
        icon: checkout,
      };
  }
};

 if (!selectedRow) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 580 }, borderRadius: 0 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#E7EBEE",
          p: 2,
          py: 1,
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              fontSize: 20,
              fontWeight: 600,
              color: "#2C384C",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {/* Show attendance date */}
            {selectedRow.date}
          </CustomText>
        </Box>
      </Box>
      <Divider />

      {/* Body */}
      <Box sx={{ px: 2, py: 2, overflowY: "auto" }}>
        {/* Punch Details */}
        <SectionHeader
          label={t("attendanceDrawer.punchDetails")}
          statusKey={getRowStatusColorKey()}
        />
        <InfoBox>
          <InfoRow label={t("attendanceDrawer.workerName")} value={selectedRow.fullName} />
          <InfoRow
            label={t("common.department")}
            value={selectedRow.department ?? selectedRow.raw?.worker?.department}
          />
          <InfoRow
            label={t("common.email")}
            value={selectedRow.email ?? selectedRow.raw?.worker?.email}
          />
          <InfoRow
            label={t("attendanceDrawer.status")}
            value={
              // <Box sx={{ pointerEvents: "none" }}>
              //   <StatusDropdown currentStatus={selectedRow.status} />
              // </Box>
                  <Chip
                                      // label={selectedRow.status} 
                                      label={formatStatus(selectedRow.status)}
                                      sx={{
                                        backgroundColor: getStatusBadgeBackgroundColor(
                                          getAttendanceHistoryColorKey(selectedRow.status)
                                        ),
                                        color: getStatusBadgeForegroundColor(
                                          getAttendanceHistoryColorKey(selectedRow.status)
                                        ),
                                        fontSize: "13px",
                                        fontWeight: 500,
                                        fontFamily: "Poppins, sans-serif",
                                        height: "30px",
                                        borderRadius: "4px",
                                      }}
                                    />
            }
          />
          <InfoRow label={t("clockIn")} value={formatTimeTo12Hour(latestClockInAt)} />
          <InfoRow label={t("clockOut")} value={formatTimeTo12Hour(latestClockOutAt)} />
          <InfoRow label={t("attendanceDrawer.location")} value={selectedRow.location} />
          <InfoRow
            label={t("attendanceDrawer.scheduledWorkingHoursLabel")}
            value={formatScheduledWorkingFromSchedule(selectedRow.raw?.details?.schedule)}
          />
          <InfoRow
            label={t("attendanceDrawer.completedWorkingHoursLabel")}
            value={formatCompletedWorkingFromSchedule(selectedRow.raw?.details?.schedule)}
          />
          <InfoRow
            label={t("attendanceDrawer.scheduledBreakAllowance")}
            value={formatScheduledBreakFromSchedule(selectedRow.raw?.details?.schedule)}
          />
          <InfoRow
            label={t("attendanceDrawer.breakTimeTaken")}
            value={formatTakenBreakFromSchedule(selectedRow.raw?.details?.schedule)}
          />
          <InfoRow
            label={t("attendanceDrawer.punchesWithIssues")}
            value={
              rowIssuesBlock?.punches_with_issues != null
                ? String(rowIssuesBlock.punches_with_issues)
                : "--"
            }
          />
          <InfoRow
            label={t("attendanceDrawer.validationIssueCount")}
            value={
              rowIssuesBlock?.validation_issue_count != null
                ? String(rowIssuesBlock.validation_issue_count)
                : "--"
            }
          />
          <InfoRow
            label={t("attendanceDrawer.scheduleIssueCount")}
            value={
              rowIssuesBlock?.schedule_issue_count != null
                ? String(rowIssuesBlock.schedule_issue_count)
                : "--"
            }
          />
          <InfoRow
            label={t("attendanceDrawer.totalIssuesCount")}
            value={
              resolveTotalIssueCount() != null
                ? String(resolveTotalIssueCount())
                : "--"
            }
          />
          <InfoRow
            label={t("issuesDetails")}
            value={formatDayIssueBreakdown()}
          />
          <InfoRow label={t("attendanceDrawer.scheduleSummary")} value={buildRowScheduleSummary()} />
        </InfoBox>

        {/* Ordered Punch Timeline */}
        {sortedOrderedPunches.length > 0 ? (
          sortedOrderedPunches.map((punch, index) => {
            const meta = getPunchMeta(punch?.actionType);
            return (
              <React.Fragment key={`punch-${punch.id || index}`}>
                <SectionHeader
                  label={meta.title}
                  statusKey={getPunchStatusForPin(punch)}
                />
                <InfoBox>
                  <DrawerInfoRow
                    label={meta.timeLabel}
                    value={formatTimeTo12Hour(punch?.occurredAt)}
                  />
                  <DrawerInfoRow
                    label={meta.locationLabel}
                    value={punch?.location?.locationText || "--"}
                  />
                  <DrawerInfoRow
                    label={t("attendanceDrawer.scheduleMatch")}
                    value={renderScheduleMatchBadge(
                      getScheduleMatchKind(punch?.schedule_timing?.classification)
                    )}
                  />
                  <DrawerInfoRow
                    label={t("attendanceDrawer.attendanceIssue", {
                      defaultValue: "Attendance Issue",
                    })}
                    value={formatScheduleClassification(
                      punch?.schedule_timing?.classification
                    )}
                  />
                  <DrawerInfoRow
                    label={t("attendanceDrawer.minutesFromSchedule")}
                    value={formatScheduleMinutesForPunch(punch)}
                  />
                  <DrawerInfoRow
                    label={t("attendanceDrawer.scheduledWindow")}
                    value={formatScheduledWindow(punch?.schedule_timing)}
                  />
                  {punchHasIssues(punch) ? (
                    <>
                      <DrawerInfoRow
                        label={t("attendanceDrawer.punchIssueCount")}
                        value={String(
                          punch?.issues?.issue_count ??
                            punch?.issues?.attendance_issues?.length ??
                            "--"
                        )}
                      />
                      {punch?.issues?.validation_issue_count != null ? (
                        <DrawerInfoRow
                          label={t("attendanceDrawer.validationIssueCount")}
                          value={String(punch.issues.validation_issue_count)}
                        />
                      ) : null}
                      {punch?.issues?.schedule_issue_count != null ? (
                        <DrawerInfoRow
                          label={t("attendanceDrawer.scheduleIssueCount")}
                          value={String(punch.issues.schedule_issue_count)}
                        />
                      ) : null}
                      <DrawerInfoRow
                        label={t("issuesDetails")}
                        value={formatPunchIssuesDetail(punch)}
                      />
                    </>
                  ) : null}
                  <EvidenceBlock punch={punch} />
                </InfoBox>
              </React.Fragment>
            );
          })
        ) : !isAbsentStatusRow ? (
          <>
            <SectionHeader label={t("clockIn")} statusKey="CLOCK_IN" />
            <InfoBox>
              <InfoRow label={t("clockIn")} value="--" />
              <InfoRow label={t("attendanceDrawer.location")} value={selectedRow.location || "--"} />
            </InfoBox>
          </>
        ) : null}

        {!isAbsentStatusRow && (
          <>
            <SectionHeader label={t("attendanceDrawer.mapView")} icon={down} />

            <InfoBox>
              {(() => {
                if (!validPunches || validPunches.length === 0) {
                  return <CustomText sx={{ fontSize: "15px" }}>{t("noLocationData")}</CustomText>;
                }
                const firstPunch = validPunches[0];

                return (
                  <Box sx={{ position: "relative" }}>
                    <GoogleMap
                      mapContainerStyle={{ width: "100%", height: "250px", borderRadius: "8px" }}
                      center={{
                        lat: firstPunch.location.gpsLatitude,
                        lng: firstPunch.location.gpsLongitude,
                      }}
                      zoom={13}
                      options={{
                        fullscreenControl: false,
                        mapTypeControl: false,
                        streetViewControl: false,
                      }}
                    >
                      {validPunches.map((punch, i) => (
                        <Marker
                          key={i}
                          position={{
                            lat: punch.location.gpsLatitude,
                            lng: punch.location.gpsLongitude,
                          }}
                          icon={getMarkerIcon(getPunchStatusForPin(punch))}
                          onClick={() => setActivePunchIndex(i)}
                        />
                      ))}
                      {activePunchIndex !== null && validPunches[activePunchIndex] && (
                        <InfoWindow
                          position={{
                            lat: validPunches[activePunchIndex].location.gpsLatitude,
                            lng: validPunches[activePunchIndex].location.gpsLongitude,
                          }}
                          onCloseClick={() => setActivePunchIndex(null)}
                        >
                          <Box sx={{ minWidth: 150 }}>
                            <Typography sx={{ fontSize: "13px" }}>
                              {validPunches[activePunchIndex].location.locationText}
                            </Typography>
                          </Box>
                        </InfoWindow>
                      )}
                    </GoogleMap>
                  </Box>
                );
              })()}
            </InfoBox>
          </>
        )}


        {/* Footer Button */}
        <Box sx={{ mt: 3 }}>
      
        </Box>
      </Box>
      
               {isFullMapOpen && (
              <Box
                sx={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  zIndex: 2000,
                  backgroundColor: "white",
                }}
              >
             
                 <IconButton
                     onClick={() => setIsFullMapOpen(false)}
                      sx={{ position: "absolute", top: 16, right: 16, zIndex: 2100,backgroundColor:"white",
                        "&:hover": { backgroundColor: "white" }
                       }}
                    >
                     <CloseIcon sx={{color:"red"}} />
                    </IconButton>
      
                {/* Full map */}
               <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={{
        lat: validPunches[0]?.location.gpsLatitude || 37.0902,
        lng: validPunches[0]?.location.gpsLongitude || -95.7129,
      }}
      zoom={15}
      onLoad={(map) => {
        fullMapRef.current = map;
        fitMapToPunches(map);
      }}
      onUnmount={() => {
        fullMapRef.current = null;
      }}
      options={{
        fullscreenControl: false,
        mapTypeControl: false,
        streetViewControl: false,
      }}
    >
      {validPunches.map((punch, i) => (
        <Marker
          key={i}
          position={{
            lat: punch.location.gpsLatitude,
            lng: punch.location.gpsLongitude,
          }}
          icon={getMarkerIcon(getPunchStatusForPin(punch))}
          onClick={() => setActivePunchIndex(i)}
        />
      ))}
      {activePunchIndex !== null && validPunches[activePunchIndex] && (
        <InfoWindow
          position={{
            lat: validPunches[activePunchIndex].location.gpsLatitude,
            lng: validPunches[activePunchIndex].location.gpsLongitude,
          }}
          onCloseClick={() => setActivePunchIndex(null)}
        >
          <Box sx={{ minWidth: 150 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>
              {validPunches[activePunchIndex].actionType}
            </Typography>
            <Typography sx={{ fontSize: "13px" }}>
              {formatTimeTo12Hour(validPunches[activePunchIndex]?.occurredAt)}
            </Typography>
            <Typography sx={{ fontSize: "13px" }}>
              {validPunches[activePunchIndex].location.locationText}
            </Typography>
          </Box>
        </InfoWindow>
      )}
    </GoogleMap>
              </Box>
            )}
    </Drawer>
  );
}






